import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import NProgress from 'nprogress'
import ToastService from '../_helpers/toast.service'
import JwtService from '../_helpers/jwt.service'
import utils from '../utils'
import i18n from '../i18n'
import { ROOT_API, APP_LOCALE } from '../_helpers/config'

// The token goes to our API only. It used to sit in axios' common headers, which every
// request carries, so a call to another address (ipapi.co once) sent it there too, and it
// stayed after logout. A relative url is the API's (baseURL); an absolute one must be on
// the API's origin
const isApiRequest = url => {
  if (!/^([a-z][a-z\d+.-]*:)?\/\//i.test(url || '')) return true
  const { origin } = window.location
  try {
    return new URL(url, origin).origin === new URL(ROOT_API, origin).origin
  } catch (error) {
    return false
  }
}

// Where a request that got a 401 sends the user. The payment page is opened from the website's
// packages, so login comes back to it, as ifAuthenticatedOrBack does for a visitor
const loginUrlAfterSessionEnd = routes => {
  const login = `/${routes.user}/${routes.login}`
  const { pathname, search } = window.location
  if (pathname.replace(/\/$/, '') !== `/${routes.user}/${routes.payment}`) return login
  return `${login}?redirect=${encodeURIComponent(pathname + search)}`
}

const ApiService = {
  init() {
    Vue.use(VueAxios, axios)
    Vue.axios.defaults.baseURL = ROOT_API

    Vue.axios.interceptors.request.use(
      config => {
        NProgress.start()
        const token = JwtService.getToken()
        if (token && isApiRequest(config.url)) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      error => {
        NProgress.done()
        return Promise.reject(error)
      }
    )
    Vue.axios.interceptors.response.use(
      res => {
        NProgress.done()
        const response = res.data
        return response
      },
      error => {
        NProgress.done()
        let errorMessage =
          APP_LOCALE === 'tr'
            ? 'Bir hata oluştu. Lütfen tekrar deneyiniz.'
            : 'An error occured. Please try again.'
        if (error.response) {
          const res = error.response
          const t = i18n.messages[process.env.VUE_APP_I18N_LOCALE]
          if (res.status === 400) {
            errorMessage = error.response.data.message
          } else if (res.status === 401) {
            JwtService.destroyAuthData()
            utils.redirectTo(loginUrlAfterSessionEnd(t.routes))
            return Promise.reject(error)
          } else if (res.status === 403) {
            utils.redirectTo(`/${t.routes.error}/${t.routes.notAllowed}`)
            return Promise.reject(error)
          }
        }
        // a page that explains the failure itself asks for no toast
        if (!(error.config && error.config.silent)) ToastService.error(errorMessage)
        return Promise.reject(error)
      }
    )
  },

  setHeader() {
    Vue.axios.defaults.headers['accept-language'] = APP_LOCALE
  },

  query(resource, params) {
    this.setHeader()
    return Vue.axios.get(resource, params)
  },

  get(resource, id = '') {
    this.setHeader()
    return Vue.axios.get(`${resource}/${id}`)
  },

  post(resource, params, config) {
    this.setHeader()
    return Vue.axios.post(`${resource}`, params, config)
  },

  put(resource, params) {
    this.setHeader()
    return Vue.axios.put(`${resource}`, params)
  },

  delete(resource, payload) {
    this.setHeader()
    return Vue.axios.delete(`${resource}`, payload)
  },

  upload(resource, formData, onProgress, config) {
    this.setHeader()
    // Content-Type is left to Axios, which adds the multipart boundary for FormData
    return Vue.axios.post(`${resource}`, formData, {
      ...config,
      onUploadProgress: onProgress
    })
  }
}

export default ApiService
